import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  inject,
  signal,
  viewChild,
  output,
} from '@angular/core';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { Subject, Subscription, debounceTime, switchMap, of } from 'rxjs';
import { MockDataService } from '../../../core/services/mock-data.service';

interface SearchResultItem {
  type: 'document' | 'memo' | 'user';
  id: string;
  title: string;
  subtitle: string;
}

interface RecentSearch {
  query: string;
  timestamp: number;
}

const RECENT_SEARCHES_KEY = 'nf_recent_searches';
const MAX_RECENT = 3;

@Component({
  selector: 'nf-search-overlay',
  standalone: true,
  imports: [MatIconModule],
  template: `
    <div class="overlay-backdrop" (click)="close.emit()"></div>
    <div class="overlay-panel">
      <!-- Search input -->
      <div class="search-box">
        <mat-icon class="search-box-icon" fontSet="material-icons-outlined">search</mat-icon>
        <input
          #searchInput
          class="search-box-input"
          type="text"
          placeholder="Search documents, memos, people..."
          (input)="onInput($event)"
          (keydown)="onKeydown($event)"
          autocomplete="off"
        />
        <kbd class="search-kbd">ESC</kbd>
      </div>

      <div class="search-body">
        @if (loading()) {
          <div class="search-loading">
            <div class="spinner"></div>
            <span>Searching...</span>
          </div>
        } @else if (query().length > 0 && !hasResults()) {
          <div class="search-empty">
            <mat-icon fontSet="material-icons-outlined">search_off</mat-icon>
            <p>No results found for "<strong>{{ query() }}</strong>"</p>
          </div>
        } @else if (query().length === 0) {
          <!-- Recent searches -->
          @if (recentSearches().length > 0) {
            <div class="search-section">
              <div class="section-header">
                <span class="section-label">Recent Searches</span>
              </div>
              @for (recent of recentSearches(); track recent.timestamp) {
                <button
                  class="result-item"
                  [class.active]="activeIndex() === recentBaseIndex + $index"
                  (click)="applyRecent(recent.query)"
                  (mouseenter)="activeIndex.set(recentBaseIndex + $index)"
                >
                  <mat-icon class="result-icon" fontSet="material-icons-outlined">history</mat-icon>
                  <span class="result-title">{{ recent.query }}</span>
                </button>
              }
            </div>
          }

          <!-- Quick actions -->
          <div class="search-section">
            <div class="section-header">
              <span class="section-label">Quick Actions</span>
            </div>
            @for (action of quickActions; track action.label) {
              <button
                class="result-item"
                [class.active]="activeIndex() === quickActionBaseIndex + $index"
                (click)="navigateTo(action.route)"
                (mouseenter)="activeIndex.set(quickActionBaseIndex + $index)"
              >
                <mat-icon class="result-icon action-icon" fontSet="material-icons-outlined">{{ action.icon }}</mat-icon>
                <span class="result-title">{{ action.label }}</span>
                <mat-icon class="result-arrow" fontSet="material-icons-outlined">arrow_forward</mat-icon>
              </button>
            }
          </div>
        } @else {
          <!-- Documents -->
          @if (documents().length > 0) {
            <div class="search-section">
              <div class="section-header">
                <span class="section-label">Documents</span>
                <span class="section-count">{{ documents().length }}</span>
              </div>
              @for (doc of documents(); track doc.id) {
                <button
                  class="result-item"
                  [class.active]="activeIndex() === getResultIndex('document', $index)"
                  (click)="selectResult(doc)"
                  (mouseenter)="activeIndex.set(getResultIndex('document', $index))"
                >
                  <mat-icon class="result-icon doc-icon" fontSet="material-icons-outlined">description</mat-icon>
                  <div class="result-content">
                    <span class="result-title">{{ doc.title }}</span>
                    <span class="result-sub">{{ doc.subtitle }}</span>
                  </div>
                </button>
              }
            </div>
          }

          <!-- Memos -->
          @if (memos().length > 0) {
            <div class="search-section">
              <div class="section-header">
                <span class="section-label">Memos</span>
                <span class="section-count">{{ memos().length }}</span>
              </div>
              @for (memo of memos(); track memo.id) {
                <button
                  class="result-item"
                  [class.active]="activeIndex() === getResultIndex('memo', $index)"
                  (click)="selectResult(memo)"
                  (mouseenter)="activeIndex.set(getResultIndex('memo', $index))"
                >
                  <mat-icon class="result-icon memo-icon" fontSet="material-icons-outlined">sticky_note_2</mat-icon>
                  <div class="result-content">
                    <span class="result-title">{{ memo.title }}</span>
                    <span class="nf-badge nf-badge--info result-badge">{{ memo.subtitle }}</span>
                  </div>
                </button>
              }
            </div>
          }

          <!-- People -->
          @if (people().length > 0) {
            <div class="search-section">
              <div class="section-header">
                <span class="section-label">People</span>
                <span class="section-count">{{ people().length }}</span>
              </div>
              @for (person of people(); track person.id) {
                <button
                  class="result-item"
                  [class.active]="activeIndex() === getResultIndex('user', $index)"
                  (click)="selectResult(person)"
                  (mouseenter)="activeIndex.set(getResultIndex('user', $index))"
                >
                  <div class="person-avatar">{{ getInitials(person.title) }}</div>
                  <div class="result-content">
                    <span class="result-title">{{ person.title }}</span>
                    <span class="result-sub">{{ person.subtitle }}</span>
                  </div>
                </button>
              }
            </div>
          }
        }
      </div>
    </div>
  `,
  styles: `
    :host {
      position: fixed;
      inset: 0;
      z-index: 1000;
      display: flex;
      justify-content: center;
      padding-top: 80px;
      animation: overlayFadeIn 150ms ease-out;
    }

    @keyframes overlayFadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .overlay-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(4px);
    }

    .overlay-panel {
      position: relative;
      width: 100%;
      max-width: 640px;
      max-height: calc(100vh - 160px);
      background: var(--nf-surface);
      border: 1px solid var(--nf-border);
      border-radius: var(--nf-radius-lg);
      box-shadow: var(--nf-shadow-lg);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      animation: panelSlideIn 200ms ease-out;
    }

    @keyframes panelSlideIn {
      from { opacity: 0; transform: translateY(-8px) scale(0.98); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }

    // Search input
    .search-box {
      display: flex;
      align-items: center;
      padding: 16px 20px;
      border-bottom: 1px solid var(--nf-border);
      gap: 12px;
    }

    .search-box-icon {
      color: var(--nf-text-muted);
      font-size: 24px;
      width: 24px;
      height: 24px;
      flex-shrink: 0;
    }

    .search-box-input {
      flex: 1;
      border: none;
      background: transparent;
      font-size: 17px;
      color: var(--nf-text);
      outline: none;
      font-family: inherit;

      &::placeholder {
        color: var(--nf-text-muted);
      }
    }

    .search-kbd {
      font-size: 11px;
      font-family: inherit;
      padding: 2px 8px;
      border-radius: 4px;
      border: 1px solid var(--nf-border);
      color: var(--nf-text-muted);
      background: var(--nf-bg);
      flex-shrink: 0;
    }

    // Body
    .search-body {
      overflow-y: auto;
      padding: 8px 0;
    }

    .search-section {
      padding: 4px 0;
    }

    .section-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 20px 4px;
    }

    .section-label {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--nf-text-muted);
    }

    .section-count {
      font-size: 11px;
      font-weight: 600;
      color: var(--nf-text-muted);
      background: var(--nf-bg);
      padding: 0 6px;
      border-radius: 100px;
      line-height: 18px;
    }

    // Result items
    .result-item {
      display: flex;
      align-items: center;
      gap: 12px;
      width: 100%;
      padding: 10px 20px;
      border: none;
      background: transparent;
      cursor: pointer;
      text-align: left;
      font-family: inherit;
      transition: background 100ms;

      &:hover, &.active {
        background: var(--nf-surface-hover);
      }
    }

    .result-icon {
      font-size: 20px;
      width: 20px;
      height: 20px;
      color: var(--nf-text-muted);
      flex-shrink: 0;
    }

    .doc-icon { color: var(--nf-info); }
    .memo-icon { color: var(--nf-accent); }
    .action-icon { color: var(--nf-primary); }

    .result-content {
      display: flex;
      flex-direction: column;
      min-width: 0;
      gap: 2px;
    }

    .result-title {
      font-size: 14px;
      font-weight: 500;
      color: var(--nf-text);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .result-sub {
      font-size: 12px;
      color: var(--nf-text-secondary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .result-badge {
      align-self: flex-start;
    }

    .result-arrow {
      margin-left: auto;
      font-size: 16px;
      width: 16px;
      height: 16px;
      color: var(--nf-text-muted);
    }

    // Person avatar
    .person-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: linear-gradient(135deg, var(--nf-primary) 0%, var(--nf-primary-light) 100%);
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 11px;
      font-weight: 600;
      flex-shrink: 0;
    }

    // Loading
    .search-loading {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 40px 20px;
      color: var(--nf-text-muted);
      font-size: 14px;
    }

    .spinner {
      width: 20px;
      height: 20px;
      border: 2px solid var(--nf-border);
      border-top-color: var(--nf-primary);
      border-radius: 50%;
      animation: spin 600ms linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    // Empty state
    .search-empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      padding: 40px 20px;
      color: var(--nf-text-muted);
      font-size: 14px;

      mat-icon {
        font-size: 40px;
        width: 40px;
        height: 40px;
        opacity: 0.5;
      }

      p { margin: 0; }
    }

    @media (max-width: 640px) {
      :host { padding-top: 16px; }
      .overlay-panel {
        max-width: calc(100% - 32px);
        max-height: calc(100vh - 32px);
      }
    }
  `,
})
export class SearchOverlayComponent implements OnInit, OnDestroy {
  readonly close = output<void>();

  private readonly router = inject(Router);
  private readonly mockData = inject(MockDataService);
  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  readonly query = signal('');
  readonly loading = signal(false);
  readonly documents = signal<SearchResultItem[]>([]);
  readonly memos = signal<SearchResultItem[]>([]);
  readonly people = signal<SearchResultItem[]>([]);
  readonly activeIndex = signal(-1);
  readonly recentSearches = signal<RecentSearch[]>([]);

  readonly quickActions = [
    { label: 'Create Document', icon: 'note_add', route: '/documents' },
    { label: 'Compose Memo', icon: 'edit_note', route: '/memos' },
    { label: 'New Workflow', icon: 'account_tree', route: '/workflows' },
  ];

  private readonly search$ = new Subject<string>();
  private subscription?: Subscription;

  // Index offsets for keyboard navigation
  readonly recentBaseIndex = 0;
  get quickActionBaseIndex(): number {
    return this.recentSearches().length;
  }

  ngOnInit(): void {
    this.loadRecentSearches();

    this.subscription = this.search$
      .pipe(
        debounceTime(300),
        switchMap(q => {
          if (!q.trim()) {
            this.documents.set([]);
            this.memos.set([]);
            this.people.set([]);
            this.loading.set(false);
            return of([]);
          }
          this.loading.set(true);
          return this.mockData.searchAll(q);
        }),
      )
      .subscribe(results => {
        this.documents.set(results.filter(r => r.type === 'document'));
        this.memos.set(results.filter(r => r.type === 'memo'));
        this.people.set(results.filter(r => r.type === 'user'));
        this.loading.set(false);
        this.activeIndex.set(-1);
      });

    // Focus the input after view init
    setTimeout(() => this.searchInput()?.nativeElement.focus(), 0);
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  hasResults(): boolean {
    return this.documents().length > 0 || this.memos().length > 0 || this.people().length > 0;
  }

  onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.query.set(value);
    this.search$.next(value);
  }

  onKeydown(event: KeyboardEvent): void {
    const total = this.getTotalNavigableItems();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.activeIndex.set((this.activeIndex() + 1) % total || 0);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.activeIndex.set(this.activeIndex() <= 0 ? total - 1 : this.activeIndex() - 1);
        break;
      case 'Enter':
        event.preventDefault();
        this.selectActiveItem();
        break;
      case 'Escape':
        this.close.emit();
        break;
    }
  }

  selectResult(item: SearchResultItem): void {
    this.saveRecentSearch(this.query());
    const routeMap: Record<string, string> = {
      document: '/documents',
      memo: '/memos',
      user: '/directory',
    };
    this.router.navigate([routeMap[item.type]]);
    this.close.emit();
  }

  navigateTo(route: string): void {
    this.router.navigate([route]);
    this.close.emit();
  }

  applyRecent(q: string): void {
    this.query.set(q);
    this.search$.next(q);
    const el = this.searchInput()?.nativeElement;
    if (el) {
      el.value = q;
      el.focus();
    }
  }

  getResultIndex(type: 'document' | 'memo' | 'user', idx: number): number {
    let offset = 0;
    if (type === 'memo') offset = this.documents().length;
    if (type === 'user') offset = this.documents().length + this.memos().length;
    return offset + idx;
  }

  getInitials(name: string): string {
    return name
      .split(' ')
      .map(n => n[0] ?? '')
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  private getTotalNavigableItems(): number {
    if (this.query().length > 0 && this.hasResults()) {
      return this.documents().length + this.memos().length + this.people().length;
    }
    // When showing recent + quick actions
    return this.recentSearches().length + this.quickActions.length;
  }

  private selectActiveItem(): void {
    const idx = this.activeIndex();
    if (idx < 0) return;

    if (this.query().length > 0 && this.hasResults()) {
      const allResults = [...this.documents(), ...this.memos(), ...this.people()];
      if (idx < allResults.length) {
        this.selectResult(allResults[idx]);
      }
    } else {
      const recentCount = this.recentSearches().length;
      if (idx < recentCount) {
        this.applyRecent(this.recentSearches()[idx].query);
      } else {
        const actionIdx = idx - recentCount;
        if (actionIdx < this.quickActions.length) {
          this.navigateTo(this.quickActions[actionIdx].route);
        }
      }
    }
  }

  private loadRecentSearches(): void {
    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (stored) {
        this.recentSearches.set(JSON.parse(stored));
      }
    } catch {
      // ignore
    }
  }

  private saveRecentSearch(q: string): void {
    const trimmed = q.trim();
    if (!trimmed) return;

    let searches = this.recentSearches();
    searches = [{ query: trimmed, timestamp: Date.now() }, ...searches.filter(s => s.query !== trimmed)].slice(0, MAX_RECENT);
    this.recentSearches.set(searches);

    try {
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(searches));
    } catch {
      // ignore
    }
  }
}
